/**
 * 첨부 파일 미리보기
 * ---------------------------------------------------------------------------
 * ## 왜 필요한가
 * 자가측정 서류와 일지 첨부는 받아서 열어 봐야 내용을 알 수 있었다. 「2026
 * 상반기 자가측정」이 맞는 파일인지 확인하려고 매번 내려받고, 내려받기
 * 폴더에는 같은 이름의 PDF 가 쌓였다. 대개는 **한 번 보고 끝낼 일**이다.
 *
 * ## 왜 링크를 그냥 열지 못하나
 * 파일 경로는 토큰이 있어야 열린다. `<iframe src="/api/...">` 로 걸면
 * 브라우저가 헤더 없이 요청해 401 이 돌아온다. 그래서 본문을 먼저 받아
 * (axios 가 토큰을 붙인다) blob 주소를 만들어 끼운다.
 *
 * ## 주소를 반드시 거둔다
 * `URL.createObjectURL` 로 만든 주소는 문서가 살아 있는 동안 메모리를 붙잡는다.
 * 창을 닫을 때마다 거두지 않으면, 서류를 여럿 열어 본 사용자의 탭이 수십 MB 씩
 * 무거워진다.
 *
 * ## 못 그리는 형식이 있다
 * PDF 와 이미지만 그린다. 그 밖의 형식(한글, 엑셀 등)은 브라우저가 열지 못하니
 * 미리보기를 흉내 내지 않고 **내려받기로 보낸다** — 빈 네모를 보여 주는 것보다
 * 낫다. 휴대전화 브라우저는 PDF 를 iframe 안에서 못 여는 경우가 있어
 * 「새 창으로 열기」를 함께 둔다.
 */

import { useEffect, useState } from 'react';
import { App as AntApp, Button, Modal, Spin, Typography } from 'antd';
import { DownloadOutlined, ExportOutlined } from '@ant-design/icons';
import { errorMessage } from '../lib/api';

const { Text, Paragraph } = Typography;

export interface PreviewTarget {
  fileName: string;
  /** 서버가 적어 둔 형식. 없으면 파일명 확장자로 짐작한다. */
  contentType?: string | null;
  /** 본문을 받아 오는 함수. 화면마다 경로가 달라 주입받는다. */
  load: () => Promise<Blob>;
}

type Kind = 'pdf' | 'image' | 'other';

/** 무엇으로 그릴 것인가. 서버 형식을 먼저 믿고, 없으면 확장자로 본다. */
function kindOf(target: PreviewTarget): Kind {
  const type = (target.contentType ?? '').toLowerCase();
  if (type.includes('pdf')) return 'pdf';
  if (type.startsWith('image/')) return 'image';

  const ext = target.fileName.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  return 'other';
}

/** 미리보기로 열 수 있는 파일인가. 단추를 보여 줄지 고를 때 쓴다. */
export function canPreview(fileName: string, contentType?: string | null): boolean {
  return kindOf({ fileName, contentType, load: async () => new Blob() }) !== 'other';
}

export function FilePreview({
  target,
  onClose,
}: {
  /** null 이면 닫힌 상태다. */
  target: PreviewTarget | null;
  onClose: () => void;
}) {
  const { message } = AntApp.useApp();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) return;

    let revoked = false;
    let made: string | null = null;

    setLoading(true);
    target
      .load()
      .then((blob) => {
        if (revoked) return;
        /*
         * 형식을 다시 씌운다. 서버가 application/octet-stream 으로 주면
         * iframe 이 그리지 않고 내려받기로 빠진다.
         */
        const kind = kindOf(target);
        const typed =
          kind === 'pdf' && blob.type !== 'application/pdf'
            ? new Blob([blob], { type: 'application/pdf' })
            : blob;

        made = URL.createObjectURL(typed);
        setUrl(made);
      })
      .catch((err) => {
        if (!revoked) message.error(errorMessage(err));
      })
      .finally(() => {
        if (!revoked) setLoading(false);
      });

    return () => {
      revoked = true;
      // 창을 닫으면 메모리를 돌려준다. 안 하면 열어 본 만큼 쌓인다.
      if (made) URL.revokeObjectURL(made);
      setUrl(null);
    };
  }, [target, message]);

  const kind = target ? kindOf(target) : 'other';

  const save = () => {
    if (!url || !target) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = target.fileName;
    a.click();
  };

  return (
    <Modal
      open={!!target}
      title={target?.fileName ?? '미리보기'}
      onCancel={onClose}
      width={900}
      destroyOnHidden
      footer={[
        <Button key="open" icon={<ExportOutlined />} disabled={!url} onClick={() => url && window.open(url, '_blank')}>
          새 창으로 열기
        </Button>,
        <Button key="save" icon={<DownloadOutlined />} disabled={!url} onClick={save}>
          내려받기
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          닫기
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div style={{ minHeight: 420 }}>
          {kind === 'other' && (
            <Paragraph type="secondary" style={{ padding: '24px 0' }}>
              이 형식은 화면에서 바로 볼 수 없습니다. <b>내려받기</b> 로 확인해 주세요.
            </Paragraph>
          )}

          {kind === 'pdf' && url && (
            <iframe
              title={target?.fileName}
              src={url}
              style={{ width: '100%', height: '70vh', border: '1px solid #f0f0f0' }}
            />
          )}

          {kind === 'image' && url && (
            <img
              alt={target?.fileName}
              src={url}
              style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
            />
          )}

          {kind !== 'other' && !url && !loading && (
            <Text type="secondary">파일을 불러오지 못했습니다.</Text>
          )}
        </div>
      </Spin>

      {kind === 'pdf' && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          화면에 안 보이면 「새 창으로 열기」를 눌러 주세요. 일부 휴대전화 브라우저는
          PDF 를 이 창 안에서 열지 못합니다.
        </Text>
      )}
    </Modal>
  );
}
